// ── Zebra ZPL label generation & printing ────────────────────────────────────
// Targets: ZDesigner ZT220-200dpi ZPL
// Label size: 3" wide × 1⅝" tall (600 × 325 dots at 200 dpi)

const STORAGE_KEY = 'zebra_printer_ip';

export function getPrinterIp(): string {
  return localStorage.getItem(STORAGE_KEY) || '';
}

export function setPrinterIp(ip: string): void {
  localStorage.setItem(STORAGE_KEY, ip.trim());
}

// ── ZPL generators ───────────────────────────────────────────────────────────

// Left margin 60 dots for text; barcode centered by calculating X offset
// Code 128 width ≈ (chars + 3) × 11 × moduleWidth + 2

/** Estimate Code 128 barcode width in dots for centering */
function bc128Width(text: string, moduleWidth = 2): number {
  // start(11) + data(11 per char) + checksum(11) + stop(13) modules
  return ((text.length + 2) * 11 + 13) * moduleWidth;
}

/** Label 1: SKU + Description + Code 128 barcode of SKU */
export function skuLabelZpl(sku: string, description: string, copies = 1): string {
  const desc = description.length > 44 ? description.slice(0, 41) + '...' : description;
  const bcX = Math.max(40, Math.round((600 - bc128Width(sku)) / 2));
  return [
    '^XA',
    '^CI28',
    '^PW600',                                          // 3" wide
    '^LL325',                                          // 1⅝" tall
    `^FO60,25^A0N,48,42^FD${sku}^FS`,                 // SKU — large
    `^FO60,82^A0N,26,24^FD${desc}^FS`,                // Description
    `^FO${bcX},125^BY2^BCN,130,Y,N,N^FD${sku}^FS`,   // Barcode — centered
    `^PQ${copies}`,
    '^XZ',
  ].join('\n');
}

/** Label 2: S/N text + Code 128 barcode of serial number */
export function snLabelZpl(serialNumber: string, copies = 1): string {
  const bcX = Math.max(40, Math.round((600 - bc128Width(serialNumber)) / 2));
  return [
    '^XA',
    '^CI28',
    '^PW600',                                                      // 3" wide
    '^LL325',                                                      // 1⅝" tall
    '^FO60,25^A0N,34,32^FDS/N:^FS',                               // "S/N:" prefix
    `^FO165,20^A0N,44,40^FD${serialNumber}^FS`,                   // Serial — large
    `^FO${bcX},125^BY2^BCN,130,Y,N,N^FD${serialNumber}^FS`,      // Barcode — centered
    `^PQ${copies}`,
    '^XZ',
  ].join('\n');
}

// ── Serial numbers helper ────────────────────────────────────────────────────

export type SerialNumbersMap = Record<string, string>; // { "1": "SN-001", "2": "SN-002" }

/** Normalise serial_numbers jsonb + legacy serial_number into a map keyed by unit */
export function normaliseSerialNumbers(
  serialNumbers: SerialNumbersMap | null | undefined,
  serialNumber: string | null | undefined,
  quantity: number,
): SerialNumbersMap {
  const map: SerialNumbersMap = {};
  for (let i = 1; i <= Math.max(quantity, 1); i++) {
    const key = String(i);
    map[key] = serialNumbers?.[key] || (i === 1 && serialNumber ? serialNumber : '');
  }
  return map;
}

// ── Send ZPL to printer ──────────────────────────────────────────────────────

export async function sendZpl(zpl: string, ip?: string): Promise<{ ok: boolean; error?: string }> {
  const printerIp = ip || getPrinterIp();
  if (!printerIp) return { ok: false, error: 'Printer IP not configured' };

  try {
    await fetch(`http://${printerIp}/pstprnt`, {
      method: 'POST',
      mode: 'no-cors',
      body: zpl,
    });
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e.message || 'Failed to reach printer' };
  }
}

